import { cva } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, type ReactNode, type Ref } from 'react';
import { cn } from '@/lib/cn';

/**
 * One row of one facet in the board filter panel (`FE-19.2`, `FE-19.3`,
 * `FE-32.5`) — finding-things' primitive, built here so the six facets do not
 * each invent their own hit area, focus ring and label association.
 *
 * ── Why `control` is not cosmetic ─────────────────────────────────────────
 * `activity` is a **scalar** on the wire — one `varchar(32)` under a CHECK, not
 * five booleans — so its options are **radios in one named group plus an "Any"
 * reset** (a radio group has no other way back to "nothing selected", and
 * `'any'` is the column's own spelling of exactly that). `members`, `labels`,
 * `due` and `completion` are arrays and are **checkboxes**. A checkbox for the
 * activity facet would let a member express `active_last_4_weeks` AND
 * `no_activity_last_4_weeks`, which denotes the empty set and is a 422.
 *
 * ── Native, not re-implemented ────────────────────────────────────────────
 * A real `<input type="checkbox">` / `<input type="radio">` inside a `<label>`.
 * No `role`, no `tabIndex`, no key handling: keyboard operation, the radio
 * group's arrow-key roving and the announcement all come for free, and they
 * come correct. `name` is **required for radios** — radios group only by shared
 * `name`, and without it arrow keys silently do nothing and two options in the
 * same facet can both be selected. The type makes that unforgettable.
 */

const facetOptionVariants = cva([
  'flex w-full cursor-pointer items-center gap-2 rounded-card px-2 py-1.5 text-left',
  'text-card text-ink hover:bg-surface-raised',
  // The row is the hit area but the INPUT is the focus target, so the visible
  // ring is pulled out to the row. This ADDS a ring; the input keeps its own
  // inherited one and nothing sets `outline-none`.
  'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
  'has-[:disabled]:cursor-not-allowed has-[:disabled]:text-ink-muted',
]);

/**
 * Reserves the leading column so rows in one facet line up even when a caller
 * has nothing to put there — the signed-out member facet renders a generic
 * avatar for a URL-borne `?members=<ULID>` it cannot name.
 */
const leadingWidths: Record<NonNullable<FacetOptionOwnProps['leadingKind']>, string> = {
  avatar: 'w-6',
  'label-chip': 'w-auto',
  swatch: 'w-3',
  none: 'w-0',
};

interface FacetOptionOwnProps {
  /**
   * The visible name of the option. A **string**, not a node: it is also the
   * accessible name, and a node cannot be composed with `secondary` into one.
   */
  label: string;
  /**
   * A count or qualifier ("12", "3 overdue"). It reaches the accessible name as
   * `"<label>, <secondary>"` — a number a member can see but not hear is
   * eyes-only, which is the same failure `IconButton.badge` guards against.
   *
   * The name is set explicitly rather than left to the label's contents,
   * because the accessible-name algorithm trims each node and then concatenates
   * without a separator: the row would announce "Priya Sharma12".
   */
  secondary?: string;
  /**
   * A slot, per the brief's cross-file rule: an `<Avatar>`, a `<LabelChip>` or
   * a colour swatch, constructed by the caller because none of them can be
   * built from an enum value. Rendered `aria-hidden` — the label text already
   * carries the name, and an avatar's own name would double it.
   */
  leading?: ReactNode;
  /** Reserves the leading column's width so rows align. Purely layout. */
  leadingKind?: 'avatar' | 'label-chip' | 'swatch' | 'none';
  className?: string;
  ref?: Ref<HTMLInputElement>;
}

type NativeInputProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'type' | 'children' | 'ref'
>;

export type FacetOptionProps =
  | (FacetOptionOwnProps & NativeInputProps & { control: 'checkbox'; name?: string })
  | (FacetOptionOwnProps & NativeInputProps & { control: 'radio'; name: string });

export function FacetOption(props: FacetOptionProps) {
  const {
    control,
    label,
    secondary,
    leading,
    leadingKind = 'none',
    className,
    ref,
    ...rest
  } = props;

  return (
    <label className={cn(facetOptionVariants(), className)}>
      <input
        type={control}
        ref={ref}
        aria-label={secondary === undefined ? undefined : `${label}, ${secondary}`}
        className="size-4 shrink-0 accent-accent"
        // Spread last, so a caller who passes `aria-label` wins. That is
        // deliberate — an explicit name from the call site is a decision, and a
        // primitive that overrode it would be swallowing a native prop.
        {...rest}
      />
      {leading !== undefined && (
        <span
          aria-hidden="true"
          className={cn('flex shrink-0 items-center', leadingWidths[leadingKind])}
        >
          {leading}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {secondary !== undefined && (
        // Muted, so it needs an opaque sheet under it — the filter panel is
        // `--color-surface-raised` for exactly this reason, but the row carries
        // its own so the constraint survives a caller that forgets.
        <span className="shrink-0 rounded bg-surface-raised px-1 text-xs text-ink-muted tabular-nums">
          {secondary}
        </span>
      )}
    </label>
  );
}
